import React, { useEffect, useState, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollManager from "./components/ScrollManager";
import PageViewTracker from "./components/PageViewTracker";
import StorageImageResolver from "./components/StorageImageResolver";

import CookieConsent from "./components/CookieConsent";
import { HelmetProvider } from "react-helmet-async";
import { supabase } from "./admin/supabase";
import { getSiteSettings } from "./siteSettings";
import Landingpage from "./LandingPage/Home";

import SiteFloats from "./SiteFloats";
import NoIndexGuard from "./components/NoIndexGuard";

// Route-level code splitting: each page ships as its own chunk so the first
// paint only downloads the code for the route being viewed.
const Programmespage = lazy(() => import("./Programmes Page/Programmes"));
const TuitionPage = lazy(() => import("./Tuition Fees/Tuitiopage"));
const Life = lazy(() => import("./Students Life/Life"));
const InstPages = lazy(() => import("./InstitutesPages/InstPages"));
const ExamMemos = lazy(() => import("./ExamMemos/ExamMemos"));
const AboutPage = lazy(() => import("./About ZU/About"));
const MissionVisionPage = lazy(() => import("./Mission, Vision and Philosophy/Mision,vision,philosophy"));
const Quality = lazy(() => import("./QualityPolicy/Quality"));
const OurCampuses = lazy(() => import("./Campuses/Ourcampuses"));
const CampusTour = lazy(() => import("./CampusTour/CampusTour"));
const Charter = lazy(() => import("./Charter and Accredition/Charter"));
const ZetechApplication = lazy(() => import("./Application/Application"));
const Feepayment = lazy(() => import("./Feepayment/Feepayment"));
const Accomodation = lazy(() => import("./Student accomodation/Accomodation"));
const FAQS = lazy(() => import("./FAQS/faqs"));
const Governance = lazy(() => import("./Governance/Governance"));
const InternationalStudents = lazy(() => import("./InternationalStudents/Internationalstudents"));
const Research = lazy(() => import("./Research/Research"));
const Conferences = lazy(() => import("./Conferences/Conferences"));
const ProgrammeDetails = lazy(() => import("./Programmes Page/ProgrammeDetails"));
const AdminApp = lazy(() => import("./admin/AdminApp"));
const Innovations = lazy(() => import("./Innovations/Innovations"));
const Foundations = lazy(() => import("./Foundations/Foundations"));
const ServiceCharter = lazy(() => import("./Service Charter/Service-charter"));
const Anthem = lazy(() => import("./ZetechAnthem/Anthem"));
const CorporateSocialResponsibility = lazy(() => import("./corporate-social-responsibility/corporate-social-responsibility"));
const OwnTestimonials = lazy(() => import("./Testimonials/Own-testimonials"));
const BlogListing = lazy(() => import("./Blog/BlogListing"));
const BlogDetail = lazy(() => import("./Blog/BlogDetail"));
const EventsListing = lazy(() => import("./Events/EventsListing"));
const EventDetail = lazy(() => import("./Events/EventDetail"));
const SitemapPage = lazy(() => import("./Sitemap/SitemapPage"));
const PrivacyPolicy = lazy(() => import("./PrivacyPolicy/PrivacyPolicy"));
const LegacyRedirect = lazy(() => import("./components/LegacyRedirect"));
const StudentForms = lazy(() => import("./StudentForms/StudentForms"));
const DataProtection = lazy(() => import("./DataProtection/DataProtection"));
const AgriTechHub = lazy(() => import("./AgriTechHub/AgriTechHub"));
const InstituteOfAgeing = lazy(() => import("./InstituteOfAgeing/InstituteOfAgeing"));
const ProfessionalCertifications = lazy(() => import("./ProfessionalCertifications/ProfessionalCertifications"));
const StudentLeadership = lazy(() => import("./Students Life/StudentLeadership"));
const ClubsAndSocieties = lazy(() => import("./Students Life/ClubsAndSocieties"));
const Studentssupport = lazy(() => import("./Students Life/Studentssupport"));
const ScholarshipsandFinancialAid = lazy(() => import("./Students Life/ScholarshipsandFinancialAid"));
const ChaplaincyAndCounseling = lazy(() => import("./Students Life/ChaplaincyAndCounseling"));
const Admissions = lazy(() => import("./SeoPages/Admissions"));
const Intakes = lazy(() => import("./SeoPages/Intakes"));
const Kuccps = lazy(() => import("./SeoPages/Kuccps"));
const StudentPortal = lazy(() => import("./SeoPages/StudentPortal"));


function App() {
    const [siteSettings, setSiteSettings] = useState(null);

    useEffect(() => {
        let isMounted = true;
        const loadSettings = async () => {
            const settings = await getSiteSettings(supabase);
            if (isMounted) {
                // NOTE: never set document.title here — <Seo> / Helmet owns the
                // per-page title, and writing it after settings load clobbered
                // every page's unique title with the bare site name.
                setSiteSettings(settings);
            }
        };

        loadSettings();
        return () => { isMounted = false; };
    }, []);

    return (
        <HelmetProvider>
        <BrowserRouter>
            <div
                className="app-shell"
                style={{
                    ...(siteSettings ? {
                        "--site-primary-color": siteSettings.primary_color || "#0F4C81",
                        "--site-secondary-color": siteSettings.secondary_color || "#F4B400",
                    } : {}),
                }}
            >
            <SiteFloats />
            <NoIndexGuard />
            <ScrollManager />
            <PageViewTracker />
            <StorageImageResolver />
            <CookieConsent />



            <Suspense fallback={<div style={{ minHeight: "60vh" }} />}>
            <Routes>

                <Route path="/" element={<Landingpage />} />

                <Route
                    path="/programmes"
                    element={<Programmespage />}
                />

                {/* Filter views are real, indexable URLs so schools,
                    levels and qualifications can be linked, shared and
                    used as ad landing pages. */}
                <Route
                    path="/programmes/school/:schoolSlug"
                    element={<Programmespage />}
                />
                <Route
                    path="/programmes/level/:levelSlug"
                    element={<Programmespage />}
                />
                <Route
                    path="/programmes/qualification/:qualificationSlug"
                    element={<Programmespage />}
                />

            

                <Route
                    path="/tuition-fees"
                    element={<TuitionPage />}
                />
                <Route
                    path="/students-life"
                    element={<Life />}
                />
                <Route
                    path="/institutes"
                    element={<InstPages />}
                />
                <Route
                    path="/exam-memos"
                    element={<ExamMemos />}
                />
                <Route
                    path="/about-zu"
                    element={<AboutPage />}
                />
                <Route
                    path="/mission-vision"
                    element={<MissionVisionPage />}
                />
                <Route
                    path="/quality-policy"
                    element={<Quality />}
                />
                <Route
                    path="/our-campuses"
                    element={<OurCampuses />}
                />
                <Route
                    path="/campus-tour"
                    element={<CampusTour />}
                />
                <Route
                    path="/charter"
                    element={<Charter />}
                />

                <Route
                    path="/application-process"
                    element={<ZetechApplication />}
                />

                {/* Legacy /apply links across the site land on the application process page */}
                <Route
                    path="/apply"
                    element={<Navigate to="/application-process" replace />}
                />

                <Route
                    path="/fee-payments"
                    element={<Feepayment />}
                />
                <Route
                    path="/student-accommodation"
                    element={<Accomodation />}
                />
                <Route
                    path="/faqs"
                    element={<FAQS />}
                />
                <Route
                    path="/governance"
                    element={<Governance />}
                />

                <Route
                    path="/international-students"
                    element={<InternationalStudents />}
                />
                 <Route
                    path="/research"
                    element={<Research />}
                />
                <Route
                    path="/institutes"
                    element={<InstPages />}
                />
                 <Route
                    path="/conferences"
                    element={<Conferences />}
                />

                 <Route
                    path="/innovations"
                    element={<Innovations />}
                />

                <Route
                    path="/our-foundation"
                    element={<Foundations />}
                />

                <Route
                    path="/programme/:slug"
                    element={<ProgrammeDetails />}
                />

                <Route
                    path="/service-charter"
                    element={<ServiceCharter />}
                />
                <Route
                    path="/zetech-anthem"
                    element={<Anthem />}
                />
                <Route
                    path="/corporate-social-responsibility"
                    element={<CorporateSocialResponsibility />}
                />
                <Route
                    path="/testimonials"
                    element={<OwnTestimonials />}
                />

                <Route path="/blog" element={<BlogListing />} />
                <Route path="/blog/:slug" element={<BlogDetail />} />

                <Route path="/events" element={<EventsListing />} />
                <Route path="/events/:slug" element={<EventDetail />} />

                <Route path="/sitemap" element={<SitemapPage />} />

                <Route path="/privacy-policy" element={<PrivacyPolicy />} />

                <Route path="/student-forms" element={<StudentForms />} />
                <Route path="/admissions" element={<Admissions />} />
                <Route path="/intakes" element={<Intakes />} />
                <Route path="/kuccps" element={<Kuccps />} />
                <Route path="/student-portal" element={<StudentPortal />} />

                <Route path="/data-protection" element={<DataProtection />} />
                <Route path="/student-leadership" element={<StudentLeadership />} />
                <Route path="/clubs&societies" element={<ClubsAndSocieties />} />
                <Route path="/student-support" element={<Studentssupport />} />
                <Route path="/scholarship&financial-aid" element={<ScholarshipsandFinancialAid />} />
                <Route path="/chaplaincy" element={<ChaplaincyAndCounseling />} />

                <Route path="/agritech-innovation-hub" element={<AgriTechHub />} />
                <Route path="/institutes/ias" element={<InstituteOfAgeing />} />
                <Route path="/ias" element={<Navigate to="/institutes/ias" replace />} />
                <Route path="/professional-certifications" element={<ProfessionalCertifications />} />
                <Route path="/cpc" element={<Navigate to="/professional-certifications" replace />} />

                <Route path="/admin/*" element={<AdminApp />} />

                {/* Catch-all: legacy (Joomla) links are redirected to the
                    matching current page; anything else shows the 404 page. */}
                <Route path="*" element={<LegacyRedirect />} />

            </Routes>
            </Suspense>

            </div>
        </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;